import { haversineMeters, Position } from '../geo/insertionIndex';

export const CONGESTION_COLORS = {
  unknown: '#4c8bf5',
  low: '#2e9e5b',
  moderate: '#e8a33d',
  heavy: '#e0563d',
  severe: '#a8261c',
};

/** Mapbox congestion_numeric is 0-100; these buckets match its documented bands. */
export function congestionColor(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return CONGESTION_COLORS.unknown;
  if (value < 40) return CONGESTION_COLORS.low;
  if (value < 60) return CONGESTION_COLORS.moderate;
  if (value < 80) return CONGESTION_COLORS.heavy;
  return CONGESTION_COLORS.severe;
}

/**
 * Build a `line-gradient` expression from per-segment congestion values.
 *
 * Stops are emitted only where the colour actually changes, which keeps the
 * expression small on long routes. Returns null when there is nothing useful
 * to draw, so the caller can fall back to a flat colour.
 */
export function buildCongestionGradient(
  coordinates: Position[],
  congestion: (number | null)[] | undefined,
): unknown[] | null {
  if (!congestion || congestion.length === 0 || coordinates.length < 2) return null;
  if (congestion.every((c) => c == null)) return null;

  const segmentLengths: number[] = [];
  let total = 0;
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    const d = haversineMeters(coordinates[i], coordinates[i + 1]);
    segmentLengths.push(d);
    total += d;
  }
  if (total <= 0) return null;

  const expression: unknown[] = ['interpolate', ['linear'], ['line-progress']];
  let cursor = 0;
  let lastColor: string | null = null;

  for (let i = 0; i < segmentLengths.length; i += 1) {
    const color = congestionColor(congestion[i]);
    if (color !== lastColor) {
      // A hard colour change needs two stops at (almost) the same offset.
      const at = Math.min(1, cursor / total);
      if (lastColor !== null) expression.push(Math.max(0, at - 1e-6), lastColor);
      expression.push(at, color);
      lastColor = color;
    }
    cursor += segmentLengths[i];
  }
  expression.push(1, lastColor ?? CONGESTION_COLORS.unknown);

  // interpolate needs at least two stops beyond the input.
  return expression.length >= 5 ? expression : null;
}
